import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { EventGuestModel } from '@app/events/shared/event-guest.model';
import { EventGuestService } from '@app/events/shared/event-guest.service';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { ToastService } from '@app/shared/toast/toast.service';

/**
 * TVG-41: inline dropdown used on the guest list row to change age group,
 * type of guest or menu without opening the full guest record. Each selection
 * saves immediately via the field's dedicated endpoint, reusing the same
 * options and permission rules as the full record. When editing is not
 * permitted it renders a plain read-only label instead of a control.
 */
type InlineSelectField = 'guest_type' | 'attendance' | 'menu';

@Component({
  selector: 'app-guest-inline-select-column',
  standalone: true,
  imports: [ReactiveFormsModule, SelectComponent],
  templateUrl: './guest-inline-select-column.component.html',
})
export class GuestInlineSelectColumnComponent implements OnChanges {
  @Input('item') public item!: EventGuestModel;
  @Input() public field!: InlineSelectField;
  /** Raw option values, sourced from the same list the full record uses. */
  @Input() public choices: string[] = [];
  @Input() public canEdit = false;
  /** Invoked after a successful save so the parent can refresh summaries. */
  @Input() public onSaved?: () => void;

  protected pending = false;
  protected readonly control = new FormControl<string | null>(null);
  protected options: { value: string; label: string }[] = [];

  private static readonly GUEST_TYPE_LABELS: Record<string, string> = {
    Adult: 'Adult (18+)',
    Teen: 'Teen (11-17)',
    Child: 'Child (2-10)',
    Baby: 'Baby (under 2)',
  };

  constructor(
    private guestService: EventGuestService,
    private toastService: ToastService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['choices'] || changes['field']) {
      this.options = this.choices.map((value) => ({ value, label: this.label(value) }));
    }

    if (changes['item'] || changes['field']) {
      this.control.setValue(this.currentValue(), { emitEvent: false });
    }
  }

  protected get clearable(): boolean {
    return this.field === 'menu';
  }

  protected get placeholder(): string {
    return this.field === 'menu' ? 'Select menu' : 'Select…';
  }

  protected displayLabel(): string {
    const value = this.currentValue();

    return value ? this.label(value) : '—';
  }

  protected async onChange(next: string | null): Promise<void> {
    const value = next ?? null;

    if (this.pending || value === this.currentValue()) {
      return;
    }

    const previous = this.currentValue();
    this.pending = true;
    this.control.disable({ emitEvent: false });

    try {
      const response = await firstValueFrom(this.save(value));
      Object.assign(this.item, response.data);
      this.toastService.success(`${this.fieldLabel()} updated.`);
      this.onSaved?.();
    } catch (error: any) {
      console.error(`Error updating ${this.field}:`, error);
      // Emit so the SelectComponent re-syncs its visible option to the reverted
      // value; without an event its internal selection would keep the rejected
      // choice on screen.
      this.control.setValue(previous, { emitEvent: true });
      this.toastService.error(this.errorMessage(error) ?? `Unable to update ${this.fieldLabel().toLowerCase()}.`);
    } finally {
      this.pending = false;
      this.control.enable({ emitEvent: false });
    }
  }

  private save(value: string | null) {
    switch (this.field) {
      case 'guest_type':
        return this.guestService.updateGuestType(this.item.event_id, this.item.id, value ?? '');
      case 'attendance':
        return this.guestService.updateAttendance(this.item.event_id, this.item.id, value ?? '');
      case 'menu':
        return this.guestService.updateMenuPreference(this.item.event_id, this.item.id, value);
    }
  }

  private currentValue(): string | null {
    switch (this.field) {
      case 'guest_type':
        return this.item?.guest_type ?? null;
      case 'attendance':
        return this.item?.attendance ?? null;
      case 'menu':
        return this.item?.dietary?.menu_preference ?? null;
    }
  }

  private label(value: string): string {
    switch (this.field) {
      case 'guest_type':
        return GuestInlineSelectColumnComponent.GUEST_TYPE_LABELS[value] ?? value;
      case 'attendance':
        return value === 'Day & Evening' ? 'Day and Evening' : value;
      case 'menu':
        return value;
    }
  }

  private fieldLabel(): string {
    switch (this.field) {
      case 'guest_type':
        return 'Age group';
      case 'attendance':
        return 'Type of guest';
      case 'menu':
        return 'Menu';
    }
  }

  private errorMessage(error: any): string | null {
    const errors = error?.error?.errors;

    if (errors && typeof errors === 'object') {
      const firstError = Object.values(errors).flat().find(Boolean);

      if (typeof firstError === 'string') {
        return firstError;
      }
    }

    return error?.error?.message ?? null;
  }
}
